import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

interface Options<Config, Editable> {
  config: Config;
  getEditable: (config: Config) => Editable;
  updateConfig: (updates: Partial<Config>) => void;
  save: (editable: Editable) => Promise<Config>;
  savedMessage: string;
  errorMessage: string;
  onError?: (error: unknown) => boolean;
  onHasChangesChange?: (hasChanges: boolean) => void;
}

const useSavedConfig = <Config, Editable extends Partial<Config>>({
  config,
  getEditable,
  updateConfig,
  save,
  savedMessage,
  errorMessage,
  onError,
  onHasChangesChange,
}: Options<Config, Editable>) => {
  const [isSaving, setIsSaving] = useState(false);
  const [savedConfig, setSavedConfig] = useState(() => getEditable(config));

  const hasChanges = useMemo(
    () => JSON.stringify(getEditable(config)) !== JSON.stringify(savedConfig),
    [config, savedConfig, getEditable]
  );

  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  const handleCancel = () => {
    if (!hasChanges) return;
    updateConfig(savedConfig);
  };

  const handleSave = () => {
    if (!hasChanges) return;
    setIsSaving(true);
    save(getEditable(config))
      .then(updated => {
        updateConfig(updated);
        setSavedConfig(getEditable(updated));
        toast.success(savedMessage);
      })
      .catch(error => {
        console.error(error);
        if (!onError?.(error)) toast.error(errorMessage);
      })
      .finally(() => setIsSaving(false));
  };

  return { hasChanges, isSaving, handleCancel, handleSave };
};

export { useSavedConfig };
